import { formatDistanceToNowStrict } from "date-fns";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { getJobLogoUrl } from "../lib/jobLogoUrl";
import type { Job } from "../types/job";

type JobCardProps = {
  job: Job;
  isHighlighted: boolean;
  isVisited: boolean;
  onHover: () => void;
  onLeave: () => void;
  /** Focus this job on the map (card click — not Apply / links). */
  onSelectOnMap: () => void;
  /** User opened the external listing (new tab). */
  onJobListingOpened: () => void;
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="m10 14 11-11" />
    </svg>
  );
}

export const JobCard = forwardRef<HTMLDivElement, JobCardProps>(
  function JobCard(
    { job, isHighlighted, isVisited, onHover, onLeave, onSelectOnMap, onJobListingOpened },
    ref,
  ) {
    const [logoOk, setLogoOk] = useState(true);
    const logoSrc = useMemo(() => getJobLogoUrl(job), [job]);

    useEffect(() => {
      setLogoOk(true);
    }, [logoSrc, job.id]);

    const activateFromCard = (target: EventTarget | null) => {
      if ((target as HTMLElement).closest("a[href]")) return;
      onSelectOnMap();
    };

    return (
      <article
        ref={ref}
        data-job-id={job.id}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={(e) => activateFromCard(e.target)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          activateFromCard(e.target);
          e.preventDefault();
        }}
        tabIndex={0}
        role="button"
        aria-label={
          isVisited
            ? `${job.title} at ${job.company}. Applied. Press Enter to show on map. Use the Applied badge to open the posting.`
            : `${job.title} at ${job.company}. Press Enter to show on map. Use Apply to open the listing.`
        }
        className={`relative rounded-xl border bg-white p-4 text-left shadow-sm transition duration-200 ease-out outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
          isHighlighted
            ? "border-primary ring-2 ring-primary/25 shadow-md"
            : "cursor-pointer border-slate-200/90 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
        }`}
      >
        <div className="flex gap-3">
          <div className="pointer-events-none flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-100 bg-slate-50 text-xs font-semibold text-slate-500">
            {logoSrc && logoOk ? (
              <img
                src={logoSrc}
                alt=""
                className="h-full w-full object-contain p-1"
                loading="lazy"
                onError={() => setLogoOk(false)}
              />
            ) : (
              <span className="text-[0.65rem] leading-tight">{initials(job.company)}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h2 className="min-w-0 flex-1 text-base font-semibold leading-snug text-slate-900">
                {job.title}
              </h2>
              <div className="shrink-0 pt-0.5">
                {isVisited ? (
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onJobListingOpened();
                    }}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-gradient-to-b from-emerald-50 to-emerald-100/80 px-2.5 py-1 text-xs font-semibold text-emerald-900 shadow-sm ring-1 ring-emerald-200/70 transition hover:from-emerald-100 hover:to-emerald-100 hover:ring-emerald-300/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-1"
                    aria-label="Applied — open job posting in a new tab"
                    title="Open posting"
                  >
                    Applied
                    <ExternalLinkIcon className="size-3 shrink-0 opacity-80" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                ) : (
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onJobListingOpened();
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-gradient-to-b from-primary to-primary-dark px-3.5 py-1.5 text-xs font-semibold tracking-wide text-white shadow-[0_2px_10px_-3px_rgba(38,99,235,0.65)] ring-1 ring-white/25 transition hover:brightness-110 hover:shadow-[0_4px_14px_-3px_rgba(38,99,235,0.55)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2"
                  >
                    Apply
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="opacity-95"
                      aria-hidden
                    >
                      <path d="M7 17L17 7M17 7H9M17 7v8" />
                    </svg>
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                )}
              </div>
            </div>
            <p className="mt-0.5 text-sm font-medium text-primary">{job.company}</p>
            <p className="mt-1 text-xs text-slate-500">{job.location}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <time dateTime={job.postedAt}>
                {formatDistanceToNowStrict(new Date(job.postedAt), { addSuffix: true })}
              </time>
              {job.salary ? (
                <>
                  <span className="text-slate-300" aria-hidden>
                    ·
                  </span>
                  <span className="font-medium text-slate-600">{job.salary}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </article>
    );
  },
);
